import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ProjectProductsServices.css';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

// Project-wide list of every room product, grouped by room, plus a section for
// manually-entered non-product charges (labor, consulting, storage, …). Each row
// has a persisted selection checkbox and an auto-derived status (Assigned when the
// item is on a Proposal, else Unassigned). Selected items can be turned into a new
// user-named Proposal in one click.
const ProjectProductsServices = ({ projectId }) => {
  const [groups, setGroups] = useState([]);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-products.php?project_id=${projectId}`);
      const data = await res.json();
      if (data.success) {
        setGroups(data.data || []);
        setCharges(data.charges || []);
      } else {
        setErr(data.message || 'Failed to load products');
      }
    } catch (e) {
      setErr('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Persist a per-item state field (selected/status) — works for both room
  // products and charges, since state is keyed by the item's id.
  const persistState = async (itemId, patch) => {
    try {
      await authFetch(`${CHAT_API_BASE}/project-products.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, room_product_id: itemId, ...patch }),
      });
    } catch (e) { /* optimistic */ }
  };

  // ── Room products ──────────────────────────────────────────────
  const setItem = (id, patch) => {
    setGroups(gs => gs.map(g => ({
      ...g,
      items: g.items.map(it => it.id === id ? { ...it, ...patch } : it),
    })));
  };
  const toggleSelect = (item) => {
    const next = !item.selected;
    setItem(item.id, { selected: next });
    persistState(item.id, { selected: next });
  };
  const changeStatus = (item, value) => {
    setItem(item.id, { status: value });
    persistState(item.id, { status: value });
  };

  // ── Manual charges ─────────────────────────────────────────────
  const setCharge = (id, patch) =>
    setCharges(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));

  const persistChargeField = async (id, patch) => {
    try {
      await authFetch(`${CHAT_API_BASE}/project-charges.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...patch }),
      });
    } catch (e) { /* optimistic */ }
  };
  const toggleChargeSelect = (ch) => {
    const next = !ch.selected;
    setCharge(ch.id, { selected: next });
    persistState(ch.id, { selected: next });
  };
  const changeChargeStatus = (ch, value) => {
    setCharge(ch.id, { status: value });
    persistState(ch.id, { status: value });
  };
  const addCharge = async () => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-charges.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, name: '', quantity: 1, unit_price: 0 }),
      });
      const data = await res.json();
      if (data.success && data.data?.id) {
        setCharges(cs => [...cs, {
          id: data.data.id, name: '', description: '', category: '',
          quantity: 1, unit_price: 0, selected: false, status: 'Unassigned', assigned: false,
        }]);
      }
    } catch (e) { alert('Failed to add charge'); }
  };
  const deleteCharge = async (id) => {
    setCharges(cs => cs.filter(c => c.id !== id));
    try {
      await authFetch(`${CHAT_API_BASE}/project-charges.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch (e) { /* already removed locally */ }
  };

  // ── Selection + proposal ───────────────────────────────────────
  const selectedProducts = useMemo(() => groups.flatMap(g => g.items.filter(it => it.selected)), [groups]);
  const selectedCharges = useMemo(() => charges.filter(c => c.selected), [charges]);
  const selectedCount = selectedProducts.length + selectedCharges.length;

  const createProposal = async () => {
    if (selectedCount === 0) return;
    const name = window.prompt('Name for the new Proposal:', 'New Proposal');
    if (name === null) return; // cancelled
    setCreating(true);
    try {
      const line_items = [
        ...selectedProducts.map(it => ({
          name: it.name || it.raw_name || 'Item',
          part_number: it.part_number || '',
          description: it.description || '',
          quantity: parseInt(it.quantity, 10) || 1,
          unit_price: parseFloat(it.price) || 0,
          product_id: it.source_product_id || null,
          room_product_id: it.id,
          taxable: true,
        })),
        ...selectedCharges.map(ch => ({
          name: ch.name || 'Charge',
          part_number: '',
          description: ch.description || '',
          quantity: parseInt(ch.quantity, 10) || 1,
          unit_price: parseFloat(ch.unit_price) || 0,
          product_id: null,
          room_product_id: ch.id,
          taxable: true,
        })),
      ];
      const expiration = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const res = await authFetch(`${CHAT_API_BASE}/project-quotes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || 'New Proposal',
          project_id: projectId,
          stage: 'Draft',
          expiration,
          line_items,
        }),
      });
      const data = await res.json();
      if (!res.ok || !(data.success || data.id)) {
        throw new Error(data.error || data.message || 'Failed to create proposal');
      }
      await fetchData(); // statuses flip to Assigned
    } catch (e) {
      alert('Failed to create proposal: ' + e.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="pps-loading"><div className="spinner" /><p>Loading products…</p></div>;
  }
  if (err) {
    return <div className="pps-error">{err}</div>;
  }

  const totalProducts = groups.reduce((n, g) => n + g.items.length, 0);
  const isEmpty = totalProducts === 0 && charges.length === 0;

  const statusSelect = (item, onChange) => {
    const val = item.status || 'Unassigned';
    const cls = val === 'Proposed' ? 'proposed'
              : val === 'Invoiced' ? 'invoiced'
              : 'unassigned';
    return (
      <select
        className={`pps-status ${cls}`}
        value={val}
        onChange={(e) => onChange(item, e.target.value)}
      >
        <option value="Unassigned">Unassigned</option>
        <option value="Proposed">Proposed</option>
        <option value="Invoiced">Invoiced</option>
      </select>
    );
  };

  return (
    <div className="project-products-services">
      <div className="pps-toolbar">
        <div className="pps-selected-count">{selectedCount} selected</div>
        <button
          className="pps-create-btn"
          onClick={createProposal}
          disabled={creating || selectedCount === 0}
          title={selectedCount === 0 ? 'Select items to include first' : 'Create a Proposal from the selected items'}
        >
          {creating ? 'Creating…' : 'Create Proposal from Selected'}
        </button>
      </div>

      {isEmpty && (
        <div className="pps-empty">
          <span className="empty-icon">📦</span>
          <h3>No Products Yet</h3>
          <p>Add products to this project's rooms, or add a manual charge below.</p>
        </div>
      )}

      {groups.map(group => (
        group.items.length === 0 ? null : (
          <div key={group.room_id} className="pps-room">
            <h3 className="pps-room-title">{group.room_name}</h3>
            <table className="pps-table">
              <thead>
                <tr>
                  <th className="pps-col-check"></th>
                  <th className="pps-col-id">ID</th>
                  <th className="pps-col-img"></th>
                  <th className="pps-col-name">Item</th>
                  <th className="pps-col-sku">SKU</th>
                  <th className="pps-col-qty">Qty</th>
                  <th className="pps-col-price">Price</th>
                  <th className="pps-col-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map(item => (
                  <tr key={item.id}>
                    <td className="pps-col-check">
                      <input type="checkbox" checked={!!item.selected} onChange={() => toggleSelect(item)} />
                    </td>
                    <td className="pps-col-id">{item.identifier || ''}</td>
                    <td className="pps-col-img">
                      {item.image_path
                        ? <img src={`/${item.image_path}`} alt="" className="pps-thumb" />
                        : <span className="pps-no-thumb">—</span>}
                    </td>
                    <td className="pps-col-name">{item.name}</td>
                    <td className="pps-col-sku">{item.part_number || ''}</td>
                    <td className="pps-col-qty">{item.quantity}</td>
                    <td className="pps-col-price">{fmtCurrency(item.price)}</td>
                    <td className="pps-col-status">{statusSelect(item, changeStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ))}

      {/* Manual, non-product charges (labor, consulting, storage, …) */}
      <div className="pps-room pps-charges">
        <div className="pps-charges-head">
          <h3 className="pps-room-title">Services &amp; Charges</h3>
          <button className="pps-add-charge-btn" onClick={addCharge}>+ Add Charge</button>
        </div>
        {charges.length === 0 ? (
          <p className="pps-room-empty">No manual charges. Use “Add Charge” to enter labor, consulting, storage, etc.</p>
        ) : (
          <table className="pps-table pps-charges-table">
            <thead>
              <tr>
                <th className="pps-col-check"></th>
                <th className="pps-col-name">Description</th>
                <th className="pps-col-cat">Category</th>
                <th className="pps-col-qty">Qty</th>
                <th className="pps-col-price">Unit Price</th>
                <th className="pps-col-status">Status</th>
                <th className="pps-col-del"></th>
              </tr>
            </thead>
            <tbody>
              {charges.map(ch => (
                <tr key={ch.id}>
                  <td className="pps-col-check">
                    <input type="checkbox" checked={!!ch.selected} onChange={() => toggleChargeSelect(ch)} />
                  </td>
                  <td className="pps-col-name">
                    <input
                      className="pps-input"
                      type="text"
                      placeholder="e.g. Labor — install"
                      value={ch.name || ''}
                      onChange={(e) => setCharge(ch.id, { name: e.target.value })}
                      onBlur={(e) => persistChargeField(ch.id, { name: e.target.value })}
                    />
                  </td>
                  <td className="pps-col-cat">
                    <input
                      className="pps-input"
                      type="text"
                      placeholder="e.g. Labor"
                      value={ch.category || ''}
                      onChange={(e) => setCharge(ch.id, { category: e.target.value })}
                      onBlur={(e) => persistChargeField(ch.id, { category: e.target.value })}
                    />
                  </td>
                  <td className="pps-col-qty">
                    <input
                      className="pps-input pps-num"
                      type="number" min="0" step="1"
                      value={ch.quantity}
                      onChange={(e) => setCharge(ch.id, { quantity: e.target.value })}
                      onBlur={(e) => persistChargeField(ch.id, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="pps-col-price">
                    <input
                      className="pps-input pps-num"
                      type="number" min="0" step="0.01"
                      value={ch.unit_price}
                      onChange={(e) => setCharge(ch.id, { unit_price: e.target.value })}
                      onBlur={(e) => persistChargeField(ch.id, { unit_price: e.target.value })}
                    />
                  </td>
                  <td className="pps-col-status">{statusSelect(ch, changeChargeStatus)}</td>
                  <td className="pps-col-del">
                    <button className="pps-del-btn" title="Remove charge" onClick={() => deleteCharge(ch.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectProductsServices;
